import {SagaIterator} from 'redux-saga';
import {call, getContext, put, putResolve} from 'redux-saga/effects';

import {
    AddressForFormType,
    createAddressForForm,
} from 'desktop/pages/checkout/utils/createAddressForForm/createAddressForForm';
import {AddressType} from 'desktop/types/addressType';

import {apiConfig} from 'libs/api/api';

import {saveEditedCustomerAddresses} from '../../../reducers/deliveryStep/customerAddresses/customerAddresses';
import {setEditStepLoading} from '../../../reducers/editStep/editStep';
import {ADDRESSES} from '../deliveryStepRootSaga/constants';
import {setFieldsAsValid} from '../setFieldsAsValid/setFieldsAsValid';

type SavingParametersType = {
    isAddressesSame: boolean;
    type: string;
};

/**
 * Disable loading for edit customer address
 * @param {boolean} isPreventLeavingCurrentLocation - prevent leaving current location
 */
export function* disableLoadingForEdit(isPreventLeavingCurrentLocation: boolean): SagaIterator {
    yield put(setEditStepLoading(false));
    if (!isPreventLeavingCurrentLocation) {
        const navigate = yield getContext('navigate');
        yield call(navigate, '/checkout/onepage');
    }
}

/**
 * Perform address saving to backend using received address
 * @param {AddressForFormType} address - Validated address to saving
 */
export function* saveEditedCustomerAddressSaga(address: AddressForFormType): SagaIterator {
    const formData = yield call(createAddressForForm, address);
    yield putResolve(saveEditedCustomerAddresses({data: formData, id: address.addressID.value}));
}

/**
 * Perform received address validation and return results
 * @param {AddressType} address - Address which will be validated
 * @param {string} type - Type of received address
 * @return {boolean} Validation result. True - in case if validation passed
 */
export function* validateAddressSaga(address: AddressType, type: string): SagaIterator {
    const updatedEditedForm = yield call(setFieldsAsValid, address);
    const saveEditedAddressAction = yield call(ADDRESSES[type].updater, updatedEditedForm);
    yield put(saveEditedAddressAction);
    return true;
}

/**
 * Sends edited customer address to backend
 * @param {AddressType} editedAddress - Edited customer address
 * @param {SavingParametersType} params - Parameters for saving and animations control
 */
export function* editSavedCustomerAddressSaga(
    editedAddress: AddressType,
    {isAddressesSame, type}: SavingParametersType,
): SagaIterator {
    // Validation of form before saving data
    const validationPassed = yield call(validateAddressSaga, editedAddress, type);
    // If validation passed preparing object for request and performing request
    if (validationPassed) {
        const {id: customerId} = apiConfig.hostStore.getState().customer;
        yield call(saveEditedCustomerAddressSaga, {
            ...editedAddress,
            customerId,
            isAddressesSame,
            region: editedAddress.state.value,
            regionID: editedAddress.regionID.value,
            type,
        } as AddressForFormType);
        yield call(disableLoadingForEdit, false);
    } else {
        yield call(disableLoadingForEdit, true);
    }
}
